import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const CollaborationPanel = ({ selectedStudent, onCollaborationUpdate }) => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [conflictResolution, setConflictResolution] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Mock active users data
  const mockActiveUsers = [
    {
      id: 'user-001',
      name: 'Sarah Johnson',
      role: 'Special Education Teacher',
      avatar: 'SJ',
      color: 'bg-primary',
      status: 'active',
      currentAction: 'Viewing incident timeline',
      lastSeen: new Date(),
      location: 'Classroom A'
    },
    {
      id: 'user-002',
      name: 'Mike Davis',
      role: 'Paraprofessional',
      avatar: 'MD',
      color: 'bg-secondary',
      status: 'editing',
      currentAction: 'Logging new incident',
      lastSeen: new Date(Date.now() - 120000), // 2 minutes ago
      location: 'Cafeteria'
    },
    {
      id: 'user-003',
      name: 'Lisa Chen',
      role: 'Behavior Specialist',
      avatar: 'LC',
      color: 'bg-accent',
      status: 'viewing',
      currentAction: 'Reviewing analytics',
      lastSeen: new Date(Date.now() - 300000), // 5 minutes ago
      location: 'Office'
    }
  ];

  // Mock recent activity data
  const mockRecentActivity = [
    {
      id: 'activity-001',
      user: 'Mike Davis',
      action: 'logged new incident',
      details: 'Verbal aggression in cafeteria',
      timestamp: new Date(Date.now() - 300000),
      type: 'incident',
      icon: 'Plus'
    },
    {
      id: 'activity-002',
      user: 'Sarah Johnson',
      action: 'updated incident details',
      details: 'Added intervention strategy notes',
      timestamp: new Date(Date.now() - 600000),
      type: 'edit',
      icon: 'Edit'
    },
    {
      id: 'activity-003',
      user: 'Lisa Chen',
      action: 'generated report',
      details: 'Weekly behavior summary',
      timestamp: new Date(Date.now() - 900000),
      type: 'report',
      icon: 'FileText'
    },
    {
      id: 'activity-004',
      user: 'Mike Davis',
      action: 'resolved conflict',
      details: 'Merged duplicate incident entries',
      timestamp: new Date(Date.now() - 1200000),
      type: 'conflict',
      icon: 'GitMerge'
    }
  ];

  // Mock conflict resolution scenario
  const mockConflict = {
    id: 'conflict-001',
    type: 'duplicate_entry',
    description: 'Two users logged the same incident simultaneously',
    incidents: [
      {
        id: 'inc-001',
        user: 'Sarah Johnson',
        time: '10:30 AM',
        behavior: 'Verbal Aggression',
        location: 'Classroom A',
        details: 'Student shouted at peer during group work'
      },
      {
        id: 'inc-002',
        user: 'Mike Davis',
        time: '10:32 AM',
        behavior: 'Verbal Aggression',
        location: 'Classroom A',
        details: 'Student raised voice during collaborative activity'
      }
    ],
    suggestedResolution: 'merge',
    timestamp: new Date(Date.now() - 180000)
  };

  useEffect(() => {
    // Simulate real-time updates
    setActiveUsers(mockActiveUsers);
    setRecentActivity(mockRecentActivity);
    
    // Simulate conflict detection
    const conflictTimer = setTimeout(() => {
      if (Math.random() > 0.7) { // 30% chance of conflict
        setConflictResolution(mockConflict);
      }
    }, 5000);

    return () => clearTimeout(conflictTimer);
  }, [selectedStudent]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success';
      case 'editing': return 'bg-warning';
      case 'viewing': return 'bg-primary';
      default: return 'bg-muted-foreground';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'incident': return 'Plus';
      case 'edit': return 'Edit';
      case 'report': return 'FileText';
      case 'conflict': return 'GitMerge';
      default: return 'Activity';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleConflictResolution = (action, conflictId) => {
    if (action === 'merge') {
      console.log('Merging incidents:', conflictId);
    } else if (action === 'keep-first') {
      console.log('Keeping first incident:', conflictId);
    } else if (action === 'keep-second') {
      console.log('Keeping second incident:', conflictId);
    }
    
    setConflictResolution(null);
    onCollaborationUpdate?.({ type: 'conflict_resolved', conflictId, action });
  };

  const inviteCollaborator = () => {
    console.log('Opening invite dialog');
    // Implementation for inviting new collaborators
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Users" size={20} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">Collaboration</h2>
              <p className="text-sm text-muted-foreground">
                {activeUsers?.length} active users
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={inviteCollaborator}>
              <Icon name="UserPlus" size={16} />
              Invite
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </Button>
          </div>
        </div>
      </div>
      {/* Conflict Resolution Alert */}
      {conflictResolution && (
        <div className="p-4 bg-warning/10 border-b border-warning/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-warning mb-1">Conflict Detected</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {conflictResolution?.description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {conflictResolution?.incidents?.map((incident, index) => (
                  <div key={incident?.id} className="p-3 bg-card rounded border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{incident?.user}</span>
                      <span className="text-xs text-muted-foreground">{incident?.time}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {incident?.behavior} - {incident?.location}
                    </p>
                    <p className="text-xs text-muted-foreground">{incident?.details}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleConflictResolution('merge', conflictResolution?.id)}
                >
                  <Icon name="GitMerge" size={14} />
                  Merge Entries
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleConflictResolution('keep-first', conflictResolution?.id)}
                >
                  Keep First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleConflictResolution('keep-second', conflictResolution?.id)}
                >
                  Keep Second
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Active Users */}
      <div className="p-4">
        <h3 className="font-medium text-card-foreground mb-3">Active Users</h3>
        <div className="space-y-3">
          {activeUsers?.map((user) => (
            <div key={user?.id} className="flex items-center gap-3">
              <div className="relative">
                <div className={`w-8 h-8 rounded-full ${user?.color} flex items-center justify-center text-white text-sm font-medium`}>
                  {user?.avatar}
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${getStatusColor(user?.status)}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm text-card-foreground truncate">
                    {user?.name}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {user?.role}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{user?.currentAction}</span>
                  <span>•</span>
                  <span>{user?.location}</span>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-xs text-muted-foreground">
                  {formatTimeAgo(user?.lastSeen)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Activity */}
      {isExpanded && (
        <div className="p-4 border-t border-border">
          <h3 className="font-medium text-card-foreground mb-3">Recent Activity</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {recentActivity?.map((activity) => (
              <div key={activity?.id} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name={getActivityIcon(activity?.type)} size={12} className="text-muted-foreground" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-card-foreground">
                    <span className="font-medium">{activity?.user}</span> {activity?.action}
                  </p>
                  <p className="text-xs text-muted-foreground">{activity?.details}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTimeAgo(activity?.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" className="text-xs">
            <Icon name="MessageSquare" size={14} />
            Team Chat
          </Button>
          <Button variant="outline" size="sm" className="text-xs">
            <Icon name="Video" size={14} />
            Quick Call
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CollaborationPanel;